import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Button,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import PdfViewerWithHighlights from "./PdfViewerWithHighlights";
import styles from "./source-verification-slider.module.scss";

interface Region {
  page_number: number;
  polygon: number[];
  page_width: number;
  page_height: number;
  unit: string;
}

interface SourceLocation {
  document_url?: string;
  page_number: number;
  polygon: number[];
  coordinate_system: string;
  page_width: number;
  page_height: number;
  unit: string;
  regions?: Region[];
}

interface ClauseReferenceWithSource {
  clause_number?: string;
  section_reference: string;
  clause_text_snippet: string;
  source_location?: SourceLocation;
  is_verified?: boolean;
}

interface DocumentWithClauses {
  documentName: string;
  documentUrl?: string;
  clauses: ClauseReferenceWithSource[];
  documentIndex: number;
}

interface SourceVerificationSliderProps {
  open: boolean;
  onClose: () => void;
  documents: DocumentWithClauses[];
  initialDocumentIndex?: number;
  initialClauseIndex?: number;
  mode?: "interactive" | "readonly";
  onVerificationChange?: (
    documentIndex: number,
    clauseIndex: number,
    isVerified: boolean,
  ) => void;
}

export default function SourceVerificationSlider({
  open,
  onClose,
  documents,
  initialDocumentIndex = 0,
  initialClauseIndex = 0,
  mode = "readonly",
  onVerificationChange,
}: SourceVerificationSliderProps): JSX.Element | null {
  const [currentDocumentIndex, setCurrentDocumentIndex] =
    useState(initialDocumentIndex);
  const [currentClauseIndex, setCurrentClauseIndex] =
    useState(initialClauseIndex);
  const [verificationStates, setVerificationStates] = useState<
    Record<number, Record<number, boolean>>
  >({});

  const currentDocument = documents[currentDocumentIndex];
  const currentClause = currentDocument?.clauses[currentClauseIndex];
  const totalDocuments = documents.length;
  const totalClauses = currentDocument?.clauses.length || 0;

  // Initialize verification states from all documents
  useEffect(() => {
    if (open) {
      const initialStates: Record<number, Record<number, boolean>> = {};
      documents.forEach((doc, docIdx) => {
        initialStates[docIdx] = {};
        doc.clauses.forEach((clause, clauseIdx) => {
          initialStates[docIdx][clauseIdx] = clause.is_verified ?? false;
        });
      });
      setVerificationStates(initialStates);
      setCurrentDocumentIndex(initialDocumentIndex);
      setCurrentClauseIndex(initialClauseIndex);
    }
  }, [open, documents, initialDocumentIndex, initialClauseIndex]);

  // Document-level navigation
  const handlePreviousDocument = () => {
    if (currentDocumentIndex > 0) {
      setCurrentDocumentIndex(currentDocumentIndex - 1);
      setCurrentClauseIndex(0); // Reset to first clause of new document
    }
  };

  const handleNextDocument = () => {
    if (currentDocumentIndex < totalDocuments - 1) {
      setCurrentDocumentIndex(currentDocumentIndex + 1);
      setCurrentClauseIndex(0); // Reset to first clause of new document
    }
  };

  // Clause-level navigation
  const handlePreviousClause = () => {
    if (currentClauseIndex > 0) {
      setCurrentClauseIndex(currentClauseIndex - 1);
    }
  };

  const handleNextClause = () => {
    if (currentClauseIndex < totalClauses - 1) {
      setCurrentClauseIndex(currentClauseIndex + 1);
    }
  };

  const handleVerificationToggle = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string | null,
  ) => {
    if (mode !== "interactive" || newValue === null) return;

    const isVerified = newValue === "verified";
    setVerificationStates((prev) => ({
      ...prev,
      [currentDocumentIndex]: {
        ...(prev[currentDocumentIndex] || {}),
        [currentClauseIndex]: isVerified,
      },
    }));
    onVerificationChange?.(
      currentDocumentIndex,
      currentClauseIndex,
      isVerified,
    );
  };

  const currentVerificationState =
    verificationStates[currentDocumentIndex]?.[currentClauseIndex] ?? false;

  // source_location is ONLY at CLAUSE level
  const sourceLocation = currentClause?.source_location;
  const documentUrl =
    sourceLocation?.document_url || currentDocument?.documentUrl;
  const hasSourceLocation = Boolean(sourceLocation && documentUrl);

  if (!currentDocument) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        className: styles.drawerPaper,
        sx: {
          width: { xs: "100%", md: "85%", lg: "80%" },
          maxWidth: "1400px",
        },
      }}
    >
      <Box className={styles.sliderContainer}>
        {/* Header - Document Navigation */}
        <Box className={styles.header}>
          <Box className={styles.headerLeft}>
            <SearchIcon sx={{ color: "#00a888", mr: 1 }} />
            <Typography variant="h6" className={styles.headerTitle}>
              Source Verification
            </Typography>
            {mode === "readonly" && (
              <Chip
                label="READ ONLY"
                size="small"
                sx={{
                  ml: 1,
                  backgroundColor: "#f5f5f5",
                  color: "#666",
                  fontWeight: 600,
                  fontSize: "11px",
                }}
              />
            )}
            {currentVerificationState && (
              <Chip
                label="✓ APPROVED"
                size="small"
                sx={{
                  ml: 1,
                  backgroundColor: "#28a745",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "11px",
                }}
              />
            )}
          </Box>
          <Box className={styles.headerRight}>
            {totalDocuments > 1 && (
              <>
                <Typography variant="body2" sx={{ color: "#666", mr: 1 }}>
                  Document {currentDocumentIndex + 1} of {totalDocuments}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handlePreviousDocument}
                  disabled={currentDocumentIndex === 0}
                  sx={{ mr: 0.5 }}
                >
                  <NavigateBeforeIcon />
                </IconButton>
                <Button
                  size="small"
                  onClick={handlePreviousDocument}
                  disabled={currentDocumentIndex === 0}
                  sx={{
                    textTransform: "none",
                    minWidth: "auto",
                    px: 1,
                    mr: 1,
                  }}
                >
                  ‹ Prev
                </Button>
                <Button
                  size="small"
                  onClick={handleNextDocument}
                  disabled={currentDocumentIndex === totalDocuments - 1}
                  sx={{
                    textTransform: "none",
                    minWidth: "auto",
                    px: 1,
                    mr: 1,
                  }}
                >
                  Next ›
                </Button>
                <IconButton
                  size="small"
                  onClick={handleNextDocument}
                  disabled={currentDocumentIndex === totalDocuments - 1}
                  sx={{ mr: 1 }}
                >
                  <NavigateNextIcon />
                </IconButton>
              </>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        {/* Main Content */}
        <Box className={styles.content}>
          {/* Left side - PDF Viewer */}
          <Box className={styles.leftPanel}>
            {/* Document Name - Always show if available */}
            {currentDocument.documentName && (
              <Box className={styles.documentInfo}>
                <DescriptionIcon sx={{ fontSize: 18, color: "#666", mr: 1 }} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#333" }}
                >
                  {currentDocument.documentName}
                </Typography>
              </Box>
            )}

            {/* PDF Viewer or Empty State */}
            {hasSourceLocation ? (
              <PdfViewerWithHighlights
                documentUrl={documentUrl!}
                sourceLocation={sourceLocation!}
                clauseNumber={currentClause?.clause_number}
                clauseIndex={currentClauseIndex}
                totalClauses={totalClauses}
              />
            ) : (
              <Box className={styles.emptyState}>
                <DescriptionIcon sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center" }}
                >
                  Source document location not available for this clause
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right side - Clause Details */}
          <Box className={styles.rightPanel}>
            <Box className={styles.clauseDetails}>
              {/* Clause Reference Section */}
              <Box className={styles.section}>
                <Typography className={styles.sectionLabel}>
                  CLAUSE REFERENCE
                </Typography>
                <Typography className={styles.clauseNumber}>
                  {currentClause?.clause_number
                    ? `Clause ${currentClause.clause_number}`
                    : currentClause?.section_reference || "N/A"}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Extracted Clause Section */}
              <Box className={styles.section}>
                <Typography className={styles.sectionLabel}>
                  EXTRACTED CLAUSE
                </Typography>
                <Box className={styles.clauseTextBox}>
                  <Typography className={styles.clauseText}>
                    {currentClause?.clause_text_snippet ||
                      "No clause text available"}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />
              <Box className={styles.section}>
                <Typography className={styles.sectionLabel}>
                  SOURCE LOCATION
                </Typography>
                {sourceLocation ? (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", display: "block" }}
                    >
                      {(() => {
                        // Show page information based on regions or single page
                        if (sourceLocation.regions && sourceLocation.regions.length > 0) {
                          const uniquePages = [...new Set(sourceLocation.regions.map(r => r.page_number))].sort((a, b) => a - b);
                          if (uniquePages.length === 1) {
                            return `Page ${uniquePages[0]}`;
                          } else {
                            return `Pages ${uniquePages.join(", ")} (${uniquePages.length} pages)`;
                          }
                        } else if (sourceLocation.page_number) {
                          return `Page ${sourceLocation.page_number}`;
                        } else {
                          return <span style={{ color: "#999", fontStyle: "italic" }}>Page number not available</span>;
                        }
                      })()}
                      {totalClauses > 1 && (
                        <span style={{ color: "#999", marginLeft: "8px" }}>
                          • Clause {currentClauseIndex + 1} of {totalClauses}
                        </span>
                      )}
                    </Typography>
                    {((sourceLocation.regions && sourceLocation.regions.length > 0) ||
                    (sourceLocation.polygon && sourceLocation.polygon.length >= 8)) ? (
                      <Typography
                        variant="caption"
                        sx={{ color: "#999", display: "block", mt: 0.5 }}
                      >
                        {sourceLocation.regions && sourceLocation.regions.length > 1
                          ? `${sourceLocation.regions.length} bounding box ${sourceLocation.regions.length === 1 ? 'region' : 'regions'} shown for precise location`
                          : "Bounding box outline shown for precise location"}
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#ff9800",
                          display: "block",
                          mt: 0.5,
                          fontStyle: "italic",
                        }}
                      >
                        Bounding box coordinates not available
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#999",
                      display: "block",
                      fontStyle: "italic",
                    }}
                  >
                    Source location not available for this clause
                  </Typography>
                )}
              </Box>

              {/* Clause Navigation - Only if multiple clauses */}
              {totalClauses > 1 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box className={styles.section}>
                    <Box className={styles.clauseNavigation}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#666",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Clause {currentClauseIndex + 1} of {totalClauses}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handlePreviousClause}
                          disabled={currentClauseIndex === 0}
                          startIcon={<NavigateBeforeIcon />}
                          sx={{
                            textTransform: "none",
                            fontSize: "12px",
                            borderColor: "#00a888",
                            color: "#00a888",
                            "&:hover": {
                              borderColor: "#00947a",
                              backgroundColor: "rgba(0, 168, 136, 0.08)",
                            },
                            "&.Mui-disabled": {
                              borderColor: "#e0e0e0",
                              color: "#999",
                            },
                          }}
                        >
                          Prev Clause
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleNextClause}
                          disabled={currentClauseIndex === totalClauses - 1}
                          endIcon={<NavigateNextIcon />}
                          sx={{
                            textTransform: "none",
                            fontSize: "12px",
                            borderColor: "#00a888",
                            color: "#00a888",
                            "&:hover": {
                              borderColor: "#00947a",
                              backgroundColor: "rgba(0, 168, 136, 0.08)",
                            },
                            "&.Mui-disabled": {
                              borderColor: "#e0e0e0",
                              color: "#999",
                            },
                          }}
                        >
                          Next Clause
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

              {/* Verification Section - Only in interactive mode */}
              {/* {mode === "interactive" && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box className={styles.section}>
                    <Typography
                      className={styles.sectionLabel}
                      sx={{ mb: 1.5 }}
                    >
                      VERIFICATION
                    </Typography>
                    <ToggleButtonGroup
                      value={
                        currentVerificationState ? "verified" : "not-verified"
                      }
                      exclusive
                      onChange={handleVerificationToggle}
                      className={styles.verificationToggle}
                      fullWidth
                    >
                      <ToggleButton
                        value="verified"
                        className={styles.verifiedButton}
                      >
                        <VerifiedIcon fontSize="small" sx={{ mr: 1 }} />
                        Verified
                      </ToggleButton>
                      <ToggleButton
                        value="not-verified"
                        className={styles.notVerifiedButton}
                      >
                        <CancelIcon fontSize="small" sx={{ mr: 1 }} />
                        Not Verified
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </>
              )} */}

              {/* Read-only verification status */}
              {/* {mode === "readonly" && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box className={styles.section}>
                    <Typography className={styles.sectionLabel}>
                      VERIFICATION STATUS
                    </Typography>
                    <Chip
                      label={
                        currentVerificationState
                          ? "✓ Verified"
                          : "○ Not Verified"
                      }
                      size="medium"
                      sx={{
                        backgroundColor: currentVerificationState
                          ? "#28a745"
                          : "#f5f5f5",
                        color: currentVerificationState ? "#fff" : "#666",
                        fontWeight: 600,
                        mt: 1,
                      }}
                    />
                  </Box>
                </>
              )} */}
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
